import React, { useEffect, Fragment } from 'react'
import { useGlobal } from "reactn";
import 'leaflet/dist/leaflet.css';
import HeaderBar from '../../../components/atoms/headerBar/HeaderBar';
import GridBackground from '../../molecules/gridBackround/GridBackground'
import styles from './About.module.scss'

const About = () => {

  const [, setshowDataLookup] = useGlobal('showDataLookup')

  // eslint-disable-next-line
  useEffect(() => {setshowDataLookup([])}, [])

  return (
    <Fragment>
      <div className="NavContainer">
        <HeaderBar />
      </div>
      <div className={styles.outer}>
      <div className={styles.content}>
        <GridBackground>
          <div className={styles.pageContent}>
          
<h1>About</h1>

<p>
With a large and overwhelmingly young population, Nigeria is considered to have the world’s greatest out of school population, which is estimated to be 10.5 million (UIS, 2017). While a variety of socio-cultural and economic factors come into play, the inaccessibility of educational facilities is often flagged as a key issue. To address this issue and school absenteeism in general, Nigeria’s <a href="https://www.ubec.gov.ng/" target="_blank"  rel="noopener noreferrer">Universal Basic Education Commission</a> (UBEC) has established a range of policies in its “Minimum Standards for Basic Education in Nigeria” plan (<a href="http://wbgfiles.worldbank.org/documents/hdn/ed/saber/supporting_doc/AFR/Nigeria/TCH/Minimum%20Standards%20for%20Basic%20Education.pdf" target="_blank" rel="noopener noreferrer">UBEC, 2010</a>). As per these standards, no student should have to travel more than two kilometres to the nearest school.  
</p>
<p>
To help Nigeria in guaranteeing education access to all, GRID3 has been working with UBEC and State Universal Basic Education Boards (SUBEBs) to apply Geographic Information Systems (GIS) to determine access to education and optimise school locations (to read the full story around this work, please click <a href="https://grid3.org/actions/gis-applications-to-determine-access-to-education-and-optimise-school-locations" target="_blank" rel="noopener noreferrer">here</a>). With these objectives in mind, GRID3 has developed this optimisation dashboard to simulate the placement of schools in locations that maximise coverage and student access across Nigeria.
</p>
<p>
This school placement optimisation tool has been built to enable UBEC, SUBEBS and other users to interpret and  interact with the outputs generated by Flowminder's method for service placement optimisation. This method utilises GRID3 Nigeria gridded population estimates to inform service delivery. 

</p>
<h2>Input data</h2>
<p>
This method relies on two key datasets: the <strong>GRID3 Nigeria gridded population estimates </strong>(WorldPop, 2019), which also contain age-sex proportions (5-9 years; 10-14 years; and all school-age children), and <strong>Nigeria's existing school data </strong>from the National Personnel Audits (NPAs).  
</p>
<p>
The locations of existing schools was collected from the National Personnel Audit (NPA). This data is released annually, and in the last update in <a href="https://grid3.org/news/how-geospatial-data-can-help-solve-nigerias-educational-challenges" target="_blank" rel="noopener noreferrer">officially released in December 2019</a>, contains geo-located national school data that includes details on each school, classifying school type, facilities available, number of teachers and attendance figures broken down by age group.
</p>    
<h2>How it works</h2>
<p>
Combined together, these input datasets enable users to carry out two activities: a school coverage assessment and an optimisation for expansion modelling. Outputs from the school coverage assessment can be utilised on their own for different analysis purposes but are required input data for the optimisation model.
</p>
<p>
The method is based on a spatial optimisation algorithm, which determines the most suitable location to build schools and maximise the number of people falling within a specified catchment area (in this context, 2km). The basics of the algorithm can be broken into the following steps:
</p>

<ul className={styles.custom}>
<li > Firstly, the algorithm assesses existing coverage rates using the location of existing schools and population estimates, based on a 2km buffer distance</li>
<li>The algorithm calculates the population outside of catchment areas to assess the population not covered by the existing schools.</li>
<li>The location of the new school is selected where there is the highest number of uncovered people within a 2km distance </li>
<li>The algorithm repeats all steps adding the location of the new facility until 100% coverage is reached.</li>

</ul>

<h3>Outputs</h3>

<p>
The dashboard is a visualisation of the school optimisation modelling, which produces two outputs:
</p>

<ul className={styles.custom}>
<li>a simulation of suggested new school locations</li>
<li> Suggestions for existing schools and new schools</li>
</ul>
<p>
Through this tool, users can interact with the data at both national and state levels. They can visualise the location of current schools (primary and/or secondary, depending on data available nationally and within each state), and the location of proposed new schools. This data can be overlaid with information on population coverage, distance to schools, population data for male and female age 5-9, population density 2km and teacher density 2km.
</p>

<h3>Data considerations</h3>

<p>
The graph below presents coverage rates compared to numbers of schools built. The schools are added in order of their number of students served in their catchment area. There are diminishing returns as more schools are built, with the first 5,000 schools positioned each having a potential population served greater than 150. However as we had more and more schools, the number of kids covered for each school diminishes, with the latest schools added covering 5 new pupils or less.
</p>
<p>

</p>
<p>
This dashboard is meant as a support tool to the Nigerian government to measure school access for children of target age groups, and plan the expansion of the school network using GIS applications, not a replacement for decision making. Addressing access does not have to necessarily translate into building a new school. This model can be combined with other methods, including considering the expansion of existing schools (e.g. adding classrooms) or by providing transportation to children living in more remote areas.
</p>

<h2>References</h2>

<p>
Pezzulo, Carla, Graeme M Hornby, Alessandro Sorichetta, Andrea E Gaughan, Catherine Linard, Tomas J Bird, David Kerr, Christopher T Lloyd, and Andrew J Tatem. 2017. “Sub-national mapping of population pyramids and dependency ratios in Africa and Asia.” Scientic Data 4 (1): 170089. <a href="https://doi.org/10.1038/sdata.2017.89">https://doi.org/10.1038/sdata.2017.89</a>.
</p>
<p>
WorldPop. 2019. Bottom-up gridded population estimates for Nigeria, version 1.2. WorldPop, University of Southampton. doi:10.5258/SOTON/WP00655
</p>
<p>
Kazeem, A., Jensen, L., & Stokes, C. S. (2010). School Attendance in Nigeria: Understanding the Impact and Intersection of Gender, Urban-Rural Residence and Socioeconomic Status. <em>Comparative education review</em>, <em>54</em>(2), 295–319. <a href="https://doi.org/10.1086/652139">https://doi.org/10.1086/652139</a>.
</p>
<p>
Shehu, H. K. (2018). Factors Influencing Primary School Non-attendance among Children in North West Nigeria. <em>Literacy Information and Computer Education Journal (LICEJ)</em>,  9(2), 2916-2922.
</p>
<p>
UBEC. (2010). Minimum Standards for Basic Education in Nigeria. Available online: <a href="https://nlipw.com/education-national-minimum-standards-establishment-institutions-act/">https://nlipw.com/education-national-minimum-standards-establishment-institutions-act/</a>.
</p>
<p>
UNESCO Institute for Statistics. (2017). Nigeria, Education and Literacy. UNESCO. Available online: <a href="https://uis.unesco.org/en/country/ng">https://uis.unesco.org/en/country/ng</a>.
</p>

<h2>About the partners</h2>


<p>
<strong>GRID3 Nigeria</strong> is part of the <a href="https://grid3.org">GRID3 global programme</a> (Geo-Referenced Infrastructure and Demographic Data for Development). GRID3  works with countries to generate, validate and use geospatial data on population, settlements, infrastructure, and administrative boundaries. GRID3 combines the expertise of partners in government, United Nations, academia, and the private sector to design adaptable and relevant geospatial solutions based on capacity and development needs of each country. 
</p>
<p>
GRID3 Nigeria aims to support the country in strengthening the application of geospatial data for evidence-based decision making. The government of Nigeria leads and manages GRID3 in the country under the guidance of the National Steering Committee, chaired by the Honorable Minister of Budget and National Planning and co-chaired by the Minister of Finance.
</p>

For GRID3 Nigeria data, visit <a href="https://grid3.gov.ng/">grid3.gov.ng</a> 


<p>
This GRID3 Nigeria school placement optimisation method and dashboard have been developed by the<a href="https://web.flowminder.org/"> Flowminder Foundation</a><strong>, </strong>an implementing partner of the GRID3 programme, in partnership with the <a href="https://www.ubec.gov.ng/">Universal Basic Education Commission</a> (UBEC). 
</p>
<p>
Flowminder is a non-profit foundation that specialises in the analysis of anonymised mobile phone data, satellite imagery, and household survey data for humanitarian and development purposes. Its mission is to enable decision makers to access the data they need to transform the lives of vulnerable people, at scale. Flowminder is striving to create a world in which decisions that can improve the lives of vulnerable people are based on the most appropriate evidence.
</p>
<p>
Web <a href="http://www.flowminder.org">www.flowminder.org</a> | Twitter <a href="http://www.twitter.com/flowminder">@Flowminder</a>
</p>



          </div>
        </GridBackground>
      </div>
      </div>
    </Fragment>
  );
}

export default About

